import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, MessageCircle, Mail } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAILTO = "cbmr71@gmail.com";
const WA_LINK = "https://wa.me/56995530858?text=Hola%20Cecilia%2C%20vi%20tu%20p%C3%A1gina%20SoytuIA%20y%20me%20gustar%C3%ADa%20saber%20m%C3%A1s";

export default function FinalCTASection() {
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    rubro: "",
    mensaje: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Save to backend
    try {
      await axios.post(`${API}/contact`, form);
    } catch (err) {
      console.error("Error saving contact:", err);
    }

    // Open mailto
    const subject = encodeURIComponent("Contacto desde SoytuIA");
    const body = encodeURIComponent(
      `Nombre: ${form.nombre}\nEmail: ${form.email}\nRubro: ${form.rubro}\nMensaje: ${form.mensaje || "Sin mensaje adicional"}`
    );
    window.location.href = `mailto:${MAILTO}?subject=${subject}&body=${body}`;

    setLoading(false);
    setSubmitted(true);
  };

  return (
    <section
      id="contacto"
      data-testid="final-cta-section"
      className="py-0 relative scroll-mt-20"
    >
      {/* Background subtle image */}
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src="https://images.unsplash.com/photo-1642854436748-233541d74ede?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxoaWdod2F5JTIwaW50ZXJzZWN0aW9uJTIwbmlnaHR8ZW58MHx8fHwxNzc2MDMyMzQxfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 w-full -mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Left: CTA Text */}
          <div className="flex flex-col justify-center">
            <h2
              data-testid="final-cta-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-3"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              ¿Listo para recuperar tu tiempo?
            </h2>
            <p className="text-sm sm:text-base text-white/60 leading-relaxed mb-6">
              Agenda una llamada gratuita de 30 minutos y te muestro cómo
              funciona.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`mailto:${MAILTO}?subject=Agendar%20llamada%20SoytuIA`}
                data-testid="cta-email-button"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B9D] text-white text-sm font-semibold hover:bg-[#FF6B9D]/80 hover:-translate-y-1 transition-transform duration-200"
              >
                <Mail size={16} />
                Escríbeme a Gmail
              </a>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full btn-secondary-outline text-sm"
              >
                <MessageCircle size={16} />
                Contáctame por WhatsApp
              </a>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="glass-surface rounded-2xl p-5 lg:p-6">
            {submitted ? (
              <div data-testid="form-success-message" className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-[#00E559]/15 flex items-center justify-center mx-auto mb-3">
                  <Send size={20} className="text-[#00E559]" />
                </div>
                <h3
                  className="text-lg font-medium text-white mb-1"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Mensaje enviado
                </h3>
                <p className="text-white/60 text-xs">
                  Te responderé lo antes posible. Revisa tu cliente de correo.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="nombre" className="text-white/80 text-xs mb-1 block">
                      Nombre
                    </Label>
                    <Input
                      id="nombre"
                      name="nombre"
                      data-testid="form-input-nombre"
                      required
                      placeholder="Tu nombre completo"
                      value={form.nombre}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 rounded-lg h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white/80 text-xs mb-1 block">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="form-input-email"
                      required
                      placeholder="tu@email.com"
                      value={form.email}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 rounded-lg h-9 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="rubro" className="text-white/80 text-xs mb-1 block">
                    Área en la que trabajas
                  </Label>
                  <Input
                    id="rubro"
                    name="rubro"
                    data-testid="form-input-rubro"
                    required
                    placeholder="Ej: educación, diseño, legal, ingeniería..."
                    value={form.rubro}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 rounded-lg h-9 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="mensaje" className="text-white/80 text-xs mb-1 block">
                    Mensaje <span className="text-white/30">(opcional)</span>
                  </Label>
                  <Textarea
                    id="mensaje"
                    name="mensaje"
                    data-testid="form-input-mensaje"
                    placeholder="Cuéntame en qué te puedo ayudar..."
                    value={form.mensaje}
                    onChange={handleChange}
                    rows={2}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 rounded-lg resize-none text-sm"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B9D] text-white text-sm font-semibold hover:bg-[#FF6B9D]/80 hover:-translate-y-1 transition-transform duration-200 disabled:opacity-50"
                >
                  <Send size={14} />
                  {loading ? "Enviando..." : "Enviar mensaje"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
